
import { ChangeDetectorRef, Component, inject } from '@angular/core';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { SAUModalService } from '@some-angular-utils/modal';
import { Urls } from '@/apiConnection/urls';
import { CustomDeleteComponent } from '@/components/custom-delete/custom-delete.component';
import { AddButtonComponent } from '@/components/add-button/add-button.component';
import { CustomSelectComponent } from '@/components/custom-select/custom-select.component';
import { TariffsFormComponent } from '../tariffs-form/tariffs-form.component';
import { TaxesFormComponent } from '../taxes-form/taxes-form.component';
import { PayMethodFormComponent } from '../pay-method-form/pay-method-form.component';
import { OfficeFormComponent } from '../office-form/office-form.component';
import { ImapFormComponent } from '../imap-form/imap-form.component';
import { VerifactuFormComponent } from '../verifactu-form/verifactu-form.component';
import { TemplateSettingsFormComponent } from '../template-settings-form/template-settings-form.component';
import { ApiService } from '@/apiConnection/ApiService';
import { ClientFormComponent } from '@/pages/clients/client-form/client-form.component';
import { TabletPairingFormComponent } from '../tablet-pairing-form/tablet-pairing-form.component';
import { DocumentDeviceFormComponent } from '../document-device-form/document-device-form.component';
import { DocumentDeviceService } from '@/offline/document-device.service';
import { DocumentDeviceMonitorService } from '@/offline/document-device-monitor.service';

@Component({
  imports: [
    ReactiveFormsModule,
    RouterModule,
    AddButtonComponent,
    CustomSelectComponent,
  ],
  templateUrl: './settings.page.html',
  styleUrl: './settings.page.scss',
})
export class SettingsPage {

  private sauModalService = inject(SAUModalService);
  protected documentDeviceService = inject(DocumentDeviceService);
  protected documentDeviceMonitor = inject(DocumentDeviceMonitorService);

  public templateSettingsShowList = false;
  public templateSettings: any[] = [];

  public tariffs: {
    invoice_tariffs: { name: string, quotient: number }[],
    ticket_tariff: number | null
  } = {
      invoice_tariffs: [],
      ticket_tariff: null
    };
  public filteredProducts: any[] = [];

  public dropdowns: any = {
    payMethods: [],
    tariffs: [],
    taxes: [],
    clients: []
  };

  public taxes: any[] = [];

  public offices: any[] = [];
  public tablets: any[] = [];

  public urlTariffs = Urls.urls.companyTariffs;
  public urlOffices = Urls.urls.offices;
  public urlTemplatesSettings = Urls.urls.templatesSettings;
  public urlTablets = Urls.urls.tablets;

  public specialClientsControl = new FormControl<number[]>([]);
  public savingSpecialClients = false;

  public clientFormComponent = ClientFormComponent

  constructor(
    private apiService: ApiService,
    private cdr: ChangeDetectorRef
  ) { }

  ngOnInit() {
    this.getDropdowns()
    this.getOffices()
    this.getTablets()
  }

  getDropdowns() {
    const config = { companies: {}, pay_methods: {}, clients: {} };
    this.apiService.getSelectors(Urls.urls.selectors, config).subscribe({
      next: (data: any) => {
        const company = data.companies[0];
        const invoice_tariffs = JSON.parse(company.invoice_tariffs || '[]').map((tariff: any) => ({
          id: tariff.id,
          name: tariff.name,
          quotient: Math.round((tariff.quotient - 1) * 100) + ' %'
        }));

        const tax_config = JSON.parse(company.tax_config || '[]');

        const ticketTariff = company.ticket_tariff ?
          [{ id: company.ticket_tariff, name: `Base (${company.ticket_tariff})` }]
          : []

        const tariffs = [
          ...ticketTariff,
          ...invoice_tariffs
        ];

        this.dropdowns = {
          payMethods: data.pay_methods || [],
          tariffs,
          taxes: tax_config,
          clients: data.clients || []
        };

        const specialClients = company.special_clients
          ? (typeof company.special_clients === 'string' ? JSON.parse(company.special_clients) : company.special_clients)
          : [];
        this.specialClientsControl.setValue(specialClients);

        this.cdr.detectChanges();
      }
    });
  }

  getOffices() {
    this.apiService.getPetition(Urls.urls.offices).subscribe({
      next: (result: any) => {
        this.offices = result.list || [];
        this.cdr.detectChanges();
      }
    });
  }

  openTariffForm() {
    const modalRef = this.sauModalService.open(TariffsFormComponent, {});

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.getDropdowns();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  openTaxForm() {
    const modalRef = this.sauModalService.open(TaxesFormComponent, {});

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.getDropdowns();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  openPaymentMethodForm(item: { id: number } = { id: 0 }) {
    const modalRef = this.sauModalService.open(PayMethodFormComponent, {});
    modalRef.componentInstance.id = item.id;

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.getDropdowns();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  openOfficeForm(office: any = null) {
    const modalRef = this.sauModalService.open(OfficeFormComponent, {});
    modalRef.componentInstance.id = office?.id || 0;
    modalRef.componentInstance.office = office;

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.getOffices();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  openOfficeDeleteForm(office: any) {
    const modalRef = this.sauModalService.open(CustomDeleteComponent, {});
    modalRef.componentInstance.id = office.id;
    modalRef.componentInstance.url = this.urlOffices;
    modalRef.componentInstance.item = 'oficina';

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.getOffices();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  getTablets() {
    this.apiService.getPetition(Urls.urls.tablets).subscribe({
      next: (result: any) => {
        this.tablets = result.list || [];
        this.cdr.detectChanges();
      }
    });
  }

  openTabletPairingForm() {
    const modalRef = this.sauModalService.open(TabletPairingFormComponent, {});
    modalRef.componentInstance.offices = this.offices;

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.getTablets();
      modalRef.dismiss();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      this.getTablets();
      modalRef.dismiss();
    });
  }

  openTabletDeleteForm(tablet: any) {
    const modalRef = this.sauModalService.open(CustomDeleteComponent, {});
    modalRef.componentInstance.id = tablet.id;
    modalRef.componentInstance.url = this.urlTablets;
    modalRef.componentInstance.item = 'tablet';

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.getTablets();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  openDocumentDeviceForm() {
    const modalRef = this.sauModalService.open(DocumentDeviceFormComponent, {});

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      modalRef.dismiss();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  openImapForm() {
    const modalRef = this.sauModalService.open(ImapFormComponent, {});

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  openVerifactuForm() {
    const modalRef = this.sauModalService.open(VerifactuFormComponent, {});

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  getTemplateSettings() {
    this.apiService.getPetition(Urls.urls.templatesSettings).subscribe({
      next: (result: any) => {
        this.templateSettings = result.list || [];
        this.cdr.detectChanges();
      }
    });
  }

  toogleTemplateSettingsList() {
    this.templateSettingsShowList = !this.templateSettingsShowList;
    if (this.templateSettingsShowList) {
      this.getTemplateSettings();
    }
  }

  openTemplateSettingsForm(template: any = null) {
    const modalRef = this.sauModalService.open(TemplateSettingsFormComponent, {});
    modalRef.componentInstance.id = template?.id || 0;
    modalRef.componentInstance.template = template;

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.getTemplateSettings();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  deleteTemplateSetting(template: any) {
    const modalRef = this.sauModalService.open(CustomDeleteComponent, {});
    modalRef.componentInstance.id = template.id;
    modalRef.componentInstance.url = this.urlTemplatesSettings;
    modalRef.componentInstance.item = 'plantilla';

    modalRef.componentInstance.acceptFunction.subscribe(() => {
      this.getTemplateSettings();
    });

    modalRef.componentInstance.closeFunction.subscribe(() => {
      modalRef.dismiss();
    });
  }

  saveSpecialClients() {
    this.savingSpecialClients = true;
    const payload = {
      special_clients: this.specialClientsControl.value || []
    };
    this.apiService.save(Urls.urls.specialClients, payload, 0).subscribe({
      next: () => {
        this.savingSpecialClients = false;
        this.cdr.detectChanges();
      },
      error: () => {
        this.savingSpecialClients = false;
        this.cdr.detectChanges();
      }
    });
  }

}
